import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Select from 'react-select';
import {
  Building2,
  Calendar,
  CreditCard,
  FileText,
  Search,
  Trash2,
  Users,
  VenusAndMars,
} from 'lucide-react';
import './PacientesIncompletos.css';
import apiService from '../services/apiService';

const PacientesIncompletos = () => {
  const { token } = useAuth();

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    unidade: null,
    nomePaciente: ''
  });

  // Estados para opções dos selects
  const [opcoesUnidade, setOpcoesUnidade] = useState([]);
  const [loadingUnidade, setLoadingUnidade] = useState(false);

  // Estados para os dados da tabela
  const [dadosTabela, setDadosTabela] = useState([]);
  const [loadingTabela, setLoadingTabela] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);

  // Estados para dashboard
  const [dadosDashboard, setDadosDashboard] = useState([]);
  const [totaisDashboard, setTotaisDashboard] = useState({
    total_unidades: 0,
    total_pacientes: 0,
    total_sem_cns: 0,
    total_sem_nome: 0,
    total_sem_data_nascimento: 0,
    total_sem_sexo: 0
  });
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  // Estados para paginação
  const [paginacao, setPaginacao] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Carregar unidades e dashboard ao montar o componente
  useEffect(() => {
    if (token) {
      carregarUnidades();
      buscarDashboard();
    }
  }, [token]);

  // Função para carregar unidades
  const carregarUnidades = async () => {
    if (!token) return;

    setLoadingUnidade(true);
    try {
      const result = await apiService.authenticatedRequest('/api/queries/unidades', {
        method: 'GET'
      });

      if (result.success) {
        const opcoes = result.data.map(item => ({
          value: item.value,
          label: item.label
        }));
        setOpcoesUnidade(opcoes);
      } else {
        console.error('Erro ao carregar unidades:', result.error);
      }
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
    } finally {
      setLoadingUnidade(false);
    }
  };

  // Função para buscar dados da API
  const buscarDados = async (page = 1) => {
    if (!token) {
      console.error('Token de autenticação não encontrado');
      return;
    }

    setLoadingTabela(true);
    
    try {
      // Sanitizar filtros
      const filtrosSanitizados = {
        unidade: filtros.unidade && typeof filtros.unidade === 'object' ? filtros.unidade.value : filtros.unidade,
        nomePaciente: filtros.nomePaciente || ''
      };

      const result = await apiService.authenticatedRequest('/api/bpa/pacientes-incompletos', {
        method: 'POST',
        body: JSON.stringify({
          page: Number(page),
          limit: Number(paginacao.limit),
          ...filtrosSanitizados
        })
      });

      if (result.success) {
        setDadosTabela(result.data);
        setTotalRegistros(result.pagination.total);
        setPaginacao(result.pagination);
      } else {
        setDadosTabela([]);
        setTotalRegistros(0);
        setPaginacao({
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        });
        console.error('Erro ao buscar dados:', result.error);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      setDadosTabela([]);
      setTotalRegistros(0);
      setPaginacao({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoadingTabela(false);
    }
  };

  // Handler para mudanças nos filtros
  const handleInputChange = (field, value) => {
    setFiltros(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para buscar dashboard
  const buscarDashboard = async () => {
    if (!token) {
      console.error('Token de autenticação não encontrado');
      return;
    }

    setLoadingDashboard(true);
    
    try {
      const result = await apiService.authenticatedRequest('/api/bpa/pacientes-incompletos/dashboard', {
        method: 'GET'
      });

      if (result.success) {
        setDadosDashboard(result.data || []);
        setTotaisDashboard(result.totais || {
          total_unidades: 0,
          total_pacientes: 0,
          total_sem_cns: 0,
          total_sem_nome: 0,
          total_sem_data_nascimento: 0,
          total_sem_sexo: 0
        });
      } else {
        console.error('Erro ao buscar dashboard:', result.error);
        setDadosDashboard([]);
      }
    } catch (error) {
      console.error('Erro ao buscar dashboard:', error);
      setDadosDashboard([]);
    } finally {
      setLoadingDashboard(false);
    }
  };

  // Função para aplicar filtros (buscar novamente)
  const handleBuscar = () => {
    buscarDados(1);
    // Dashboard não é atualizado ao buscar - apenas carrega na primeira vez
  };

  // Função para limpar filtros
  const handleLimpar = () => {
    setFiltros({
      unidade: null,
      nomePaciente: ''
    });
    // Buscar novamente após limpar (dashboard não é atualizado)
    setTimeout(() => {
      buscarDados(1);
    }, 100);
  };

  // Função para formatar data
  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    try {
      // Se for um número (formato AAAAMMDD), converter
      if (typeof dataString === 'number') {
        const str = dataString.toString();
        if (str.length === 8) {
          const ano = str.substring(0, 4);
          const mes = str.substring(4, 6);
          const dia = str.substring(6, 8);
          return `${dia}/${mes}/${ano}`;
        }
      }
      const data = new Date(dataString);
      if (isNaN(data.getTime())) return 'N/A';
      return data.toLocaleDateString('pt-BR');
    } catch {
      return 'N/A';
    }
  };

  // Função para identificar quais campos estão faltando
  const camposFaltantes = (item) => {
    const faltantes = [];
    // Verificar CNS - está faltando se for null, vazio ou 'N/A'
    if (!item.cns || item.cns === 'N/A' || item.cns === '' || item.cns.trim() === '') faltantes.push('CNS');
    if (!item.nome || item.nome === 'N/A') faltantes.push('Nome');
    if (!item.dt_nascimento) faltantes.push('Data Nascimento');
    if (!item.sexo || item.sexo === 'N/A') faltantes.push('Sexo');
    return faltantes.length > 0 ? faltantes.join(', ') : 'Nenhum';
  };

  // Funções de paginação
  const handlePaginaAnterior = () => {
    if (paginacao.hasPrev && !loadingTabela) {
      const novaPagina = paginacao.page - 1;
      buscarDados(novaPagina);
    }
  };

  const handleProximaPagina = () => {
    if (paginacao.hasNext && !loadingTabela) {
      const novaPagina = paginacao.page + 1;
      buscarDados(novaPagina);
    }
  };

  const handleIrParaPagina = (pagina) => {
    if (pagina >= 1 && pagina <= paginacao.totalPages && !loadingTabela && pagina !== paginacao.page) {
      buscarDados(pagina);
    }
  };

  return (
    <div className="pacientes-incompletos">
      <div className="page-header">
        <h1>Pacientes com Dados Incompletos</h1>
        <p>Listagem de pacientes que possuem dados obrigatórios faltantes para geração de BPA</p>
      </div>

      {/* Dashboard Section */}
      <div className="dashboard-section">
        <h2 className="dashboard-title">Dashboard por Unidade</h2>
        
        {loadingDashboard ? (
          <div className="dashboard-loading">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <span>Carregando dashboard...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Cards de Totais Gerais */}
            <div className="dashboard-totais">
              <div className="dashboard-card total">
                <div className="card-icon flex h-10 w-10 items-center justify-center rounded-xl bg-primary-light text-primary">
                  <Building2 size={20} />
                </div>
                <div className="card-content">
                  <h3>{totaisDashboard.total_unidades}</h3>
                  <p>Unidades com Pacientes Incompletos</p>
                </div>
              </div>
              
              <div className="dashboard-card pacientes">
                <div className="card-icon flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                  <Users size={20} />
                </div>
                <div className="card-content">
                  <h3>{totaisDashboard.total_pacientes}</h3>
                  <p>Total de Pacientes Incompletos</p>
                </div>
              </div>
              
              <div className="dashboard-card sem-cns">
                <div className="card-icon flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  <CreditCard size={20} />
                </div>
                <div className="card-content">
                  <h3>{totaisDashboard.total_sem_cns}</h3>
                  <p>Sem CNS</p>
                </div>
              </div>
              
              <div className="dashboard-card sem-nome">
                <div className="card-icon flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                  <FileText size={20} />
                </div>
                <div className="card-content">
                  <h3>{totaisDashboard.total_sem_nome}</h3>
                  <p>Sem Nome</p>
                </div>
              </div>
              
              <div className="dashboard-card sem-data">
                <div className="card-icon flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <Calendar size={20} />
                </div>
                <div className="card-content">
                  <h3>{totaisDashboard.total_sem_data_nascimento}</h3>
                  <p>Sem Data Nascimento</p>
                </div>
              </div>
              
              <div className="dashboard-card sem-sexo">
                <div className="card-icon flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                  <VenusAndMars size={20} />
                </div>
                <div className="card-content">
                  <h3>{totaisDashboard.total_sem_sexo}</h3>
                  <p>Sem Sexo</p>
                </div>
              </div>
            </div>

            {/* Tabela por Unidade */}
            {dadosDashboard.length > 0 ? (
              <div className="dashboard-table-container">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Unidade</th>
                      <th>Total Pacientes</th>
                      <th>Sem CNS</th>
                      <th>Sem Nome</th>
                      <th>Sem Data Nasc.</th>
                      <th>Sem Sexo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dadosDashboard.map((item, index) => (
                      <tr key={index}>
                        <td className="unidade-nome">{item.unidade_nome}</td>
                        <td className="total-cell">
                          <strong>{item.total_pacientes}</strong>
                        </td>
                        <td className={item.sem_cns > 0 ? 'campo-faltante' : ''}>
                          {item.sem_cns > 0 ? item.sem_cns : '-'}
                        </td>
                        <td className={item.sem_nome > 0 ? 'campo-faltante' : ''}>
                          {item.sem_nome > 0 ? item.sem_nome : '-'}
                        </td>
                        <td className={item.sem_data_nascimento > 0 ? 'campo-faltante' : ''}>
                          {item.sem_data_nascimento > 0 ? item.sem_data_nascimento : '-'}
                        </td>
                        <td className={item.sem_sexo > 0 ? 'campo-faltante' : ''}>
                          {item.sem_sexo > 0 ? item.sem_sexo : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dashboard-empty">
                <p>Nenhuma unidade com pacientes incompletos encontrada.</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="filtros-section">
        <div className="filtros-grid">
          <div className="filtro-group">
            <label>Unidade</label>
            <Select
              value={filtros.unidade}
              onChange={(option) => handleInputChange('unidade', option)}
              options={opcoesUnidade}
              placeholder={loadingUnidade ? "Carregando unidades..." : "Selecionar unidade..."}
              isSearchable
              isClearable
              isLoading={loadingUnidade}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          <div className="filtro-group">
            <label>Nome do Paciente</label>
            <input
              type="text"
              value={filtros.nomePaciente}
              onChange={(e) => handleInputChange('nomePaciente', e.target.value)}
              placeholder="Digite o nome do paciente..."
              className="input-text"
            />
          </div>
        </div>

        <div className="filtros-actions">
          <button className="action-btn buscar" onClick={handleBuscar}>
            <Search size={16} />
            Buscar
          </button>
          <button className="action-btn limpar" onClick={handleLimpar}>
            <Trash2 size={16} />
            Limpar
          </button>
        </div>
      </div>

      <div className="resultados-section">
        {totalRegistros > 0 && (
          <div className="table-info">
            <span className="total-registros">
              {totalRegistros} registro{totalRegistros !== 1 ? 's' : ''} encontrado{totalRegistros !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        <div className="tabela-container">
          <table className="resultados-tabela">
            <thead>
              <tr>
                <th>ID</th>
                <th>CNS</th>
                <th>Nome</th>
                <th>Data Nascimento</th>
                <th>Sexo</th>
                <th>Unidade</th>
                <th>Campos Faltantes</th>
              </tr>
            </thead>
            <tbody>
              {loadingTabela ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                      <span>Carregando dados...</span>
                    </div>
                  </td>
                </tr>
              ) : dadosTabela.length > 0 ? (
                dadosTabela.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <strong style={{ color: '#4299e1' }}>
                        {item.paciente_id || 'N/A'}
                      </strong>
                    </td>
                    <td>{item.cns || <span className="text-danger">Faltante</span>}</td>
                    <td>{item.nome || <span className="text-danger">Faltante</span>}</td>
                    <td>{formatarData(item.dt_nascimento)}</td>
                    <td>{item.sexo || <span className="text-danger">Faltante</span>}</td>
                    <td>{item.unidade || 'N/A'}</td>
                    <td>
                      <span className="campos-faltantes">
                        {camposFaltantes(item)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="no-data">
                    <div>
                      <span>Nenhum registro encontrado</span>
                      <p>Todos os pacientes estão com dados completos!</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {paginacao.totalPages > 0 && (
          <div className="paginacao">
            <div className="pagination-info">
              <span>
                Mostrando {((paginacao.page - 1) * paginacao.limit) + 1} a {Math.min(paginacao.page * paginacao.limit, paginacao.total)} de {paginacao.total} registros
              </span>
            </div>
            
            <div className="pagination-controls">
              <button 
                className={`pagination-btn ${!paginacao.hasPrev || loadingTabela ? 'disabled' : ''}`}
                onClick={handlePaginaAnterior}
                disabled={!paginacao.hasPrev || loadingTabela}
              >
                « Anterior
              </button>
              
              <div className="pagination-numbers">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, paginacao.page - Math.floor(maxVisible / 2));
                  let endPage = Math.min(paginacao.totalPages, startPage + maxVisible - 1);
                  
                  if (endPage - startPage + 1 < maxVisible) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }
                  
                  // Primeira página
                  if (startPage > 1) {
                    pages.push(
                      <button key={1} className="page-number" onClick={() => handleIrParaPagina(1)}>
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
                    }
                  }
                  
                  // Páginas do meio
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button 
                        key={i} 
                        className={`page-number ${i === paginacao.page ? 'active' : ''} ${loadingTabela ? 'disabled' : ''}`}
                        onClick={() => handleIrParaPagina(i)}
                        disabled={loadingTabela}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  // Última página
                  if (endPage < paginacao.totalPages) {
                    if (endPage < paginacao.totalPages - 1) {
                      pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
                    }
                    pages.push(
                      <button 
                        key={paginacao.totalPages} 
                        className="page-number" 
                        onClick={() => handleIrParaPagina(paginacao.totalPages)}
                      >
                        {paginacao.totalPages}
                      </button>
                    );
                  }
                  
                  return pages;
                })()}
              </div>
              
              <button 
                className={`pagination-btn ${!paginacao.hasNext || loadingTabela ? 'disabled' : ''}`}
                onClick={handleProximaPagina}
                disabled={!paginacao.hasNext || loadingTabela}
              >
                Próxima »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PacientesIncompletos;

